import { StockService } from './stockService';

// For development, we'll use manual price updates
// In production, replace with Alpha Vantage or Yahoo Finance API

export interface StockQuote {
  symbol: string;
  price: number;
  change: number;
  changePercent: number;
  volume: number;
  timestamp: Date;
}

export class StockPriceService {
  // Alpha Vantage API configuration
  private static API_KEY = process.env.REACT_APP_ALPHA_VANTAGE_API_KEY || 'demo';
  private static BASE_URL = 'https://www.alphavantage.co/query';

  // For demo/development - simulate price movements
  static async getQuote(symbol: string): Promise<StockQuote> {
    // In development, simulate price movements
    if (!process.env.REACT_APP_ALPHA_VANTAGE_API_KEY || process.env.REACT_APP_ALPHA_VANTAGE_API_KEY === 'demo') {
      return this.simulatePrice(symbol);
    }

    // Real API call (Alpha Vantage)
    try {
      const response = await fetch(
        `${this.BASE_URL}?function=GLOBAL_QUOTE&symbol=${symbol}&apikey=${this.API_KEY}`
      );
      const data = await response.json();
      
      if (data['Global Quote']) {
        const quote = data['Global Quote'];
        return {
          symbol,
          price: parseFloat(quote['05. price']),
          change: parseFloat(quote['09. change']),
          changePercent: parseFloat(quote['10. change percent'].replace('%', '')),
          volume: parseInt(quote['06. volume']),
          timestamp: new Date()
        };
      }
      
      // Fallback to simulation if API fails
      return this.simulatePrice(symbol);
    } catch (error) {
      console.error('Error fetching stock price:', error);
      return this.simulatePrice(symbol);
    }
  }

  // Simulate realistic price movements for testing
  private static simulatePrice(symbol: string): StockQuote {
    // Generate consistent base price from symbol - same price for same symbol
    const hash = symbol.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
    const basePrice = (hash % 400) + 20;
    
    // Add minimal daily movement based on current date (not random)
    const dayOfYear = Math.floor((new Date().getTime() - new Date(new Date().getFullYear(), 0, 0).getTime()) / 86400000);
    const dayFactor = Math.sin(dayOfYear * 0.1 + hash) * 0.02; // +/- 2% variation
    const price = basePrice * (1 + dayFactor);
    const change = basePrice * dayFactor;
    
    return {
      symbol,
      price: Math.round(price * 100) / 100,
      change: Math.round(change * 100) / 100,
      changePercent: Math.round(dayFactor * 10000) / 100,
      volume: 1000000 + (hash * 1000),
      timestamp: new Date()
    };
  }

  // Update all watchlist prices
  static async updateWatchlistPrices(symbols: string[]): Promise<void> {
    const updatePromises = symbols.map(async (symbol) => {
      const quote = await this.getQuote(symbol);
      await StockService.updateStockPrice(symbol, quote.price);
    });

    await Promise.all(updatePromises);
  }

  // Manual price update for testing
  static async manualPriceUpdate(symbol: string, newPrice: number): Promise<void> {
    await StockService.updateStockPrice(symbol, newPrice);
  }

  // Get current price for a single symbol
  static async getCurrentPrice(symbol: string): Promise<number> {
    const quote = await this.getQuote(symbol);
    return quote.price;
  }
}